import React, { useState, useEffect, useRef } from 'react';
import { Volume2, ArrowRight } from 'lucide-react';

interface WordCardProps {
  word: string;
  pronunciation?: string;
  translation: string;
  persian_translation?: string;
  examples: Array<{
    dutch: string;
    english: string;
    persian?: string;
  }>;
  onNext: () => void;
  nativeLanguage: string;
}

export function WordCard({ word, pronunciation, translation, persian_translation, examples, onNext, nativeLanguage }: WordCardProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSpeechSupported, setIsSpeechSupported] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const speechSynthesisRef = useRef<SpeechSynthesisUtterance | null>(null);

  useEffect(() => {
    // Check if speech synthesis is supported
    if (!window.speechSynthesis) {
      console.warn('Speech synthesis not supported in this browser');
      setIsSpeechSupported(false);
      return;
    }

    // Create a function to ensure voices are loaded
    const ensureVoicesLoaded = () => {
      return new Promise<SpeechSynthesisVoice[]>((resolve) => {
        const voices = window.speechSynthesis.getVoices();
        if (voices.length > 0) {
          resolve(voices);
        } else {
          // Wait for voices to be loaded
          window.speechSynthesis.onvoiceschanged = () => {
            resolve(window.speechSynthesis.getVoices());
          };
        }
      });
    };

    // Initialize speech synthesis
    const initializeSpeechSynthesis = async () => {
      try {
        const voices = await ensureVoicesLoaded();
        console.log('Available voices:', voices.map(v => `${v.name} (${v.lang})`));
        
        // Look for Dutch voices
        const dutchVoices = voices.filter(voice => 
          voice.lang.toLowerCase().includes('nl') || 
          voice.name.toLowerCase().includes('dutch') ||
          voice.name.toLowerCase().includes('nederlands')
        );
        
        console.log('Dutch voices:', dutchVoices.map(v => `${v.name} (${v.lang})`));

        // Create utterance with Dutch language
        speechSynthesisRef.current = new SpeechSynthesisUtterance();
        speechSynthesisRef.current.lang = 'nl-NL';
        speechSynthesisRef.current.rate = 0.9;
        
        // Try to use a Dutch voice if available
        if (dutchVoices.length > 0) {
          speechSynthesisRef.current.voice = dutchVoices[0];
          console.log('Using Dutch voice:', dutchVoices[0].name);
        }

        speechSynthesisRef.current.onend = () => {
          setIsPlaying(false);
        };

        speechSynthesisRef.current.onerror = (event) => {
          console.error('Speech synthesis error:', event);
          setIsPlaying(false);
          setIsSpeechSupported(false);
        };
        
        // iOS requires speech synthesis to be initialized by user interaction
        const initializeSpeech = () => {
          if (!isInitialized) {
            const tempUtterance = new SpeechSynthesisUtterance('');
            tempUtterance.lang = 'nl-NL';
            window.speechSynthesis.speak(tempUtterance);
            setIsInitialized(true);
          }
        };

        // Listen for touch events to initialize speech on mobile
        document.addEventListener('touchstart', initializeSpeech);
        document.addEventListener('click', initializeSpeech);
        
        return () => {
          document.removeEventListener('touchstart', initializeSpeech);
          document.removeEventListener('click', initializeSpeech);
        };
      } catch (error) {
        console.error('Error initializing speech synthesis:', error);
        setIsSpeechSupported(false);
      }
    };

    initializeSpeechSynthesis();
    
    // Cleanup function
    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const playAudio = (text: string) => {
    if (!window.speechSynthesis) {
      console.warn('Speech synthesis not supported');
      setIsSpeechSupported(false);
      return;
    }

    if (isPlaying) {
      window.speechSynthesis.cancel();
      setIsPlaying(false);
      return;
    }

    try {
      // Cancel any ongoing speech
      window.speechSynthesis.cancel();
      
      // Create a new utterance each time to ensure settings are applied
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'nl-NL';
      utterance.rate = 0.9;
      
      // Try to get Dutch voices
      const voices = window.speechSynthesis.getVoices();
      const dutchVoices = voices.filter(voice => 
        voice.lang.toLowerCase().includes('nl') || 
        voice.name.toLowerCase().includes('dutch') ||
        voice.name.toLowerCase().includes('nederlands')
      );
      
      // Use a Dutch voice if available
      if (dutchVoices.length > 0) {
        utterance.voice = dutchVoices[0];
      }
      
      utterance.onend = () => {
        setIsPlaying(false);
      };
      
      utterance.onerror = (event) => {
        console.error('Speech synthesis error during playback:', event);
        setIsPlaying(false);
      };
      
      setIsPlaying(true);
      window.speechSynthesis.speak(utterance);
      
      // On some mobile browsers, speech synthesis can stop unexpectedly
      // This timeout ensures the isPlaying state is reset if onend doesn't fire
      setTimeout(() => {
        setIsPlaying(false);
      }, 10000); // 10 seconds max for any speech
      
    } catch (error) {
      console.error('Error playing audio:', error);
      setIsPlaying(false);
      setIsSpeechSupported(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => playAudio(word)}
              className="p-2 text-gray-500 hover:text-gray-700 transition-colors rounded-full hover:bg-gray-100"
            >
              <Volume2 className="h-6 w-6" />
            </button>
            <div>
              <h2 className="text-3xl font-bold text-gray-900">{word}</h2>
              {pronunciation && (
                <p className="text-gray-500 mt-1">/{pronunciation}/</p>
              )}
            </div>
          </div>
          <p className="text-xl text-gray-600">
            {nativeLanguage === 'Persian' && persian_translation
              ? persian_translation 
              : translation}
          </p>
        </div>

        <div className="space-y-6 pt-6 border-t">
          <h3 className="text-lg font-semibold text-gray-900">Example Sentences</h3>
          <div className="space-y-4">
            {examples.map((example, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <button
                    onClick={() => playAudio(example.dutch)}
                    className={`text-gray-500 hover:text-gray-700 transition-colors ${
                      !isSpeechSupported && 'opacity-50 cursor-not-allowed'
                    }`}
                    disabled={!isSpeechSupported}
                    title={!isSpeechSupported ? 'Speech synthesis not supported on this device' : undefined}
                  >
                    <Volume2 className="h-5 w-5" />
                  </button>
                  <p className="text-lg font-medium text-gray-900">{example.dutch}</p>
                </div>
                <p className="text-gray-600 italic ml-9">
                  {nativeLanguage === 'Persian' && example.persian ? example.persian : example.english}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end pt-4">
          <button
            onClick={onNext}
            className="flex items-center px-6 py-3 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 transition-colors"
          >
            Continue to Practice
            <ArrowRight className="h-5 w-5 ml-2" />
          </button>
        </div>
      </div>
    </div>
  );
}